/**
 * PlatformKillswitchBanner — site-wide banner driven by:
 *   - Founder's `platform.maintenance_mode` / `platform.read_only` killswitch flags
 *   - Scheduled maintenance windows (warning countdown + active notice)
 *
 * Priority: killswitch maintenance > active window > killswitch read-only > warning window.
 * Fetches initial state via GET /platform/status. Subscribes to WS /ws/platform and
 * refetches on `platform.maintenance_window_changed` events.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Lock, Clock } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { platformAPI } from '../../api';

const WS_PATH = '/api/ws/platform';

function buildWsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (!base) return null;
  return base.replace(/^http/, 'ws') + WS_PATH;
}

function formatCountdown(targetIso) {
  if (!targetIso) return '';
  const ms = new Date(targetIso).getTime() - Date.now();
  if (ms <= 0) return 'starting now';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `in ${h}h ${m}m`;
  }
  if (minutes >= 1) return `in ${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  return `in ${seconds}s`;
}

export default function PlatformKillswitchBanner() {
  const { user } = useAuth();
  const [state, setState] = useState({
    killswitch: {
      'platform.maintenance_mode': { enabled: false },
      'platform.read_only': { enabled: false },
    },
    windows: { active: [], warning: [] },
  });
  const [, setTick] = useState(0); // drives the countdown re-render
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await platformAPI.status();
      setState((prev) => ({
        killswitch: { ...prev.killswitch, ...(data?.killswitch || {}) },
        windows: data?.maintenance_windows || { active: [], warning: [] },
      }));
    } catch {
      /* non-fatal — banner stays hidden */
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const connect = () => {
      const token = localStorage.getItem('token');
      const base = buildWsUrl();
      if (!base) return;
      const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
      let ws;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'platform.snapshot' && msg.data?.killswitch) {
            setState((prev) => ({ ...prev, killswitch: { ...prev.killswitch, ...msg.data.killswitch } }));
          } else if (msg.type === 'platform.flag_changed' && msg.data?.flag_key) {
            setState((prev) => ({
              ...prev,
              killswitch: {
                ...prev.killswitch,
                [msg.data.flag_key]: {
                  enabled: !!msg.data.enabled,
                  name: msg.data.name,
                  description: msg.data.description,
                  updated_at: msg.data.updated_at,
                },
              },
            }));
          } else if (msg.type === 'platform.maintenance_window_changed') {
            // A transition or cancellation happened server-side — simplest
            // correct update is to refetch the snapshot.
            bootstrap();
          }
        } catch { /* ignore malformed */ }
      };
      ws.onclose = () => {
        if (cancelled) return;
        reconnectRef.current = setTimeout(connect, 5000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    bootstrap();
    connect();

    // Tick every second so the countdown refreshes.
    const intervalId = setInterval(() => setTick((t) => t + 1), 1000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) try { wsRef.current.close(); } catch { /* noop */ }
    };
  }, [user, bootstrap]);

  if (!user) return null;

  const maintenance = !!state.killswitch['platform.maintenance_mode']?.enabled;
  const readOnly = !!state.killswitch['platform.read_only']?.enabled;
  const activeWindow = state.windows.active?.[0] || null;
  const warningWindow = state.windows.warning?.[0] || null;

  // Priority-ordered resolver: pick exactly one banner to show at a time.
  let banner = null;
  if (maintenance) {
    banner = {
      mode: 'maintenance',
      icon: AlertTriangle,
      tone: 'bg-rose-600 text-white',
      title: 'Platform maintenance in progress',
      body: state.killswitch['platform.maintenance_mode']?.description
        || 'Some features are temporarily unavailable. Your changes may not save until maintenance ends.',
    };
  } else if (activeWindow) {
    banner = {
      mode: 'window-active',
      icon: AlertTriangle,
      tone: 'bg-rose-600 text-white',
      title: activeWindow.title || 'Scheduled maintenance in progress',
      body: activeWindow.message || 'This window is active now. Some features may be unavailable.',
      extra: `ends ${formatCountdown(activeWindow.ends_at)}`,
    };
  } else if (readOnly) {
    banner = {
      mode: 'read-only',
      icon: Lock,
      tone: 'bg-amber-500 text-stone-900',
      title: 'Platform in read-only mode',
      body: state.killswitch['platform.read_only']?.description
        || 'The platform is read-only right now. New submissions are paused.',
    };
  } else if (warningWindow) {
    banner = {
      mode: 'window-warning',
      icon: Clock,
      tone: 'bg-sky-600 text-white',
      title: `Upcoming maintenance: ${warningWindow.title || 'scheduled window'}`,
      body: warningWindow.message || 'A scheduled maintenance window is about to begin.',
      extra: `starts ${formatCountdown(warningWindow.starts_at)}`,
    };
  }

  if (!banner) return null;
  const { icon: Icon, tone, title, body, extra, mode } = banner;

  return (
    <div
      data-testid="platform-killswitch-banner"
      data-mode={mode}
      className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium ${tone}`}
      role="alert"
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div className="truncate">
        <b>{title}</b>
        <span className="ml-2 opacity-90">{body}</span>
        {extra && (
          <span
            className="ml-2 px-2 py-0.5 rounded bg-black/15 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="platform-killswitch-countdown"
          >
            {extra}
          </span>
        )}
      </div>
    </div>
  );
}
